import * as Sentry from "@sentry/react";

export function initSentry(config) {
  Sentry.init({
    ...config,
    enabled: process.env.NODE_ENV === "production", // This prevents Sentry from monitoring in development mode
    normalizeDepth: 5,
    integrations: [
      new Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
  });
}

/**
 * The part of the Redux state that is sent to Sentry with every error.
 *
 * The store holds volunteers' and callers' personal data (names, phones,
 * addresses, call notes) and lists that can be megabytes big, so none of it
 * is sent. Only the current route and which request each slice is waiting on.
 *
 * @param {object} state - The full Redux state.
 * @returns {{ route: string|null, loading: Object<string, string|boolean> }}
 */
export function reduxStateForSentry(state) {
  const { router, ...slices } = state || {};
  const loading = {};

  Object.entries(slices).forEach(([sliceName, slice]) => {
    const sliceLoading = slice && slice.loading;
    if (sliceLoading === true) {
      loading[sliceName] = true;
    } else if (sliceLoading && typeof sliceLoading.source === "string") {
      loading[sliceName] = sliceLoading.source;
    }
  });

  return {
    route: router?.location?.pathname ?? null,
    loading,
  };
}

/**
 * The Redux action breadcrumb sent to Sentry: only its type, never its
 * payload, which can be a whole list of volunteers or a caller's details.
 *
 * @param {object} action - The dispatched Redux action.
 * @param {string[]} actionsToIgnore - Action types that get no breadcrumb.
 * @returns {{ type: string }|null} The breadcrumb data, or null to skip it.
 */
export function reduxActionForSentry(action, actionsToIgnore = []) {
  if (actionsToIgnore.includes(action.type)) {
    return null;
  }
  return { type: action.type };
}

/**
 * Sets up Sentry for Redux. Only action types and a small summary of the
 * state with no personal data are sent (see the two functions above).
 *
 * @param {string[]} actionsToIgnore - An array of action types to ignore.
 * @returns {Function} A Redux enhancer that integrates with Sentry.
 */
export function setupSentryForRedux(actionsToIgnore) {
  return Sentry.createReduxEnhancer({
    actionTransformer: (action) =>
      reduxActionForSentry(action, actionsToIgnore),
    stateTransformer: reduxStateForSentry,
  });
}
